"use client";

/**
 * tour-icon-map.tsx
 *
 * Single source of truth for all icons used in the guided tour and the
 * profile Help section.  Every icon is taken DIRECTLY from the sidebar nav
 * (sidebar-nav.tsx) so the visual language is identical everywhere.
 */

import React from "react";
import {
  Home, UserCircle2, LayoutGrid, ShoppingCart, MessageSquare,
  Radio, Vault, Music, Headphones, Heart, BarChart, BarChart2,
  Mic2, Palette, LogIn, CheckCheck, Wand2, Wallet, CalendarDays,
  Newspaper, BookOpen, Brain, Settings, Film,
} from "lucide-react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LucideIconComponent = React.ComponentType<any>;

/**
 * Maps every tour step ID → the Lucide icon used in the sidebar nav.
 * New step IDs must be added here and nowhere else.
 */
export const STEP_ICON_MAP: Record<string, LucideIconComponent> = {
  // ── Music Maker tour ────────────────────────────────────────────────────────
  "maker-welcome":     Mic2,
  "maker-sidebar":     LayoutGrid,
  "maker-profile":     UserCircle2,
  "maker-media-vault": Vault,
  "maker-releases":    Music,
  "maker-marketplace": ShoppingCart,
  "maker-stats":       BarChart,
  "maker-earnings":    BarChart2,
  "maker-artwork":     Wand2,
  "maker-radio":       Radio,
  "maker-party":       Headphones,
  "maker-community":   MessageSquare,
  "maker-done":        CheckCheck,

  // ── Music Lover tour ────────────────────────────────────────────────────────
  "lover-welcome":     Headphones,
  "lover-sidebar":     LayoutGrid,
  "lover-home":        Home,
  "lover-favorites":   Heart,
  "lover-player":      Headphones,
  "lover-artists":     Mic2,
  "lover-marketplace": ShoppingCart,
  "lover-radio":       Radio,
  "lover-party":       Music,
  "lover-community":   MessageSquare,
  "lover-profile":     UserCircle2,
  "lover-done":        CheckCheck,

  // ── Guest tour ───────────────────────────────────────────────────────────────
  "guest-welcome":     Headphones,
  "guest-sidebar":     LayoutGrid,
  "guest-home":        Home,
  "guest-favorites":   Heart,
  "guest-player":      Headphones,
  "guest-themes":      Palette,
  "guest-radio":       Radio,
  "guest-signin":      LogIn,

  // ── Legacy generic tour (kept for backward compat) ──────────────────────────
  "welcome":           Music,
  "sidebar":           LayoutGrid,
  "home-feed":         Home,
  "media-player":      Headphones,
  "favorites-bar":     Heart,
  "audio-meters":      BarChart2,
  "artists":           Mic2,
  "music-page":        Music,
  "media-vault":       Vault,
  "marketplace":       ShoppingCart,
  "community":         MessageSquare,
  "radio":             Radio,
  "profile":           UserCircle2,
  "listening-party":   Music,
  "help":              CheckCheck,

  // ── Extra sidebar pages (used in help section page meta) ─────────────────────
  "events":            CalendarDays,
  "news":              Newspaper,
  "wallet":            Wallet,
  "bookkeeping":       BookOpen,
  "coach":             Brain,
  "settings":          Settings,
  "film-maker":        Film,
};

/**
 * Renders the Lucide icon for a given tour step ID.
 * Used identically in GuidedTour card headers AND HelpSection step cards.
 */
export function StepIcon({
  stepId,
  size = 22,
  className = "",
}: {
  stepId: string;
  size?: number;
  className?: string;
}) {
  const Icon = STEP_ICON_MAP[stepId];
  if (!Icon) return null;

  return (
    <div
      className={`flex-shrink-0 flex items-center justify-center rounded-xl bg-primary/10 border border-primary/20 ${className}`}
      style={{ width: size + 14, height: size + 14 }}
    >
      <Icon
        className="text-primary"
        style={{ width: size, height: size }}
        strokeWidth={1.5}
      />
    </div>
  );
}
